import { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

// Placeholders map to the backend's Handlebars fields.
const PLACEHOLDERS = [
  { value: "displayName", label: "Full name" },
  { value: "firstName", label: "First name" },
  { value: "lastName", label: "Last name" },
  { value: "jobTitle", label: "Job title" },
  { value: "department", label: "Department" },
  { value: "phone", label: "Phone" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
  { value: "officeLocation", label: "Office location" },
  { value: "officeAddressLine", label: "Office address (by location)" },
  { value: "officePhone", label: "Office phone (by location)" },
  { value: "companyName", label: "Company name" },
  { value: "addressLine", label: "Company address" },
  { value: "website", label: "Website" },
  { value: "brandColor", label: "Brand color" },
  { value: "logoUrl", label: "Logo URL" },
];

/**
 * Self-hosted TinyMCE signature editor with placeholder, conditional-placeholder
 * and template menus. Output is email-safe HTML stored verbatim (no cleanup).
 *
 * Props: value, onChange(html), templates[], onOpenTemplate(t), onSaveAsTemplate(html)
 */
export default function SignatureEditor({ value, onChange, templates = [], onOpenTemplate, onSaveAsTemplate }) {
  // Keep latest props reachable from TinyMCE's setup() closures.
  const tplRef = useRef(templates);
  const openRef = useRef(onOpenTemplate);
  const saveRef = useRef(onSaveAsTemplate);
  tplRef.current = templates;
  openRef.current = onOpenTemplate;
  saveRef.current = onSaveAsTemplate;

  return (
    <Editor
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      value={value}
      onEditorChange={(html) => onChange(html)}
      init={{
        license_key: "gpl",
        height: 460,
        menubar: false,
        promotion: false,
        branding: false,
        statusbar: true,
        plugins: "table image link code lists advlist autolink",
        toolbar: [
          "fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify",
          "bullist numlist | table image link | placeholder condplaceholder | tpls | code",
        ],
        toolbar_mode: "wrap",
        font_family_formats:
          "Arial=arial,helvetica,sans-serif; Calibri=calibri,sans-serif; Georgia=georgia,serif; Montserrat=montserrat,'Segoe UI',arial,sans-serif; Tahoma=tahoma,sans-serif; Times New Roman=times new roman,serif; Verdana=verdana,sans-serif",
        font_size_formats: "8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt",
        // Preserve email HTML exactly — no tag/style stripping or URL rewriting.
        verify_html: false,
        valid_elements: "*[*]",
        extended_valid_elements: "*[*]",
        convert_urls: false,
        relative_urls: false,
        // Load Montserrat inside the editor iframe so picking it renders correctly.
        content_style:
          "@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');" +
          "body{font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#333;}",
        setup: (editor) => {
          editor.ui.registry.addMenuButton("placeholder", {
            text: "Placeholder",
            tooltip: "Insert a dynamic field",
            fetch: (cb) =>
              cb(
                PLACEHOLDERS.map((p) => ({
                  type: "menuitem",
                  text: p.label,
                  onAction: () => editor.insertContent(`{{${p.value}}}`),
                }))
              ),
          });

          editor.ui.registry.addMenuButton("condplaceholder", {
            text: "Conditional",
            tooltip: "Insert a field that disappears when empty",
            fetch: (cb) =>
              cb(
                PLACEHOLDERS.map((p) => ({
                  type: "menuitem",
                  text: `${p.label} (hide if blank)`,
                  onAction: () => editor.insertContent(`{{#if ${p.value}}}{{${p.value}}}{{/if}}`),
                }))
              ),
          });

          editor.ui.registry.addMenuButton("tpls", {
            text: "Templates",
            tooltip: "Open or save a template",
            fetch: (cb) => {
              const openItems = (tplRef.current || []).map((t) => ({
                type: "menuitem",
                text: t.name,
                onAction: () => openRef.current && openRef.current(t),
              }));
              cb([
                {
                  type: "nestedmenuitem",
                  text: "Open template",
                  getSubmenuItems: () =>
                    openItems.length ? openItems : [{ type: "menuitem", text: "(none)", onAction: () => {} }],
                },
                {
                  type: "menuitem",
                  text: "Save as new template",
                  onAction: () => saveRef.current && saveRef.current(editor.getContent()),
                },
                {
                  type: "menuitem",
                  text: "Save to file (.html)",
                  onAction: () => downloadHtml(editor.getContent()),
                },
                {
                  type: "menuitem",
                  text: "Load from file…",
                  onAction: () => loadHtml((html) => editor.setContent(html)),
                },
              ]);
            },
          });
        },
      }}
    />
  );
}

function downloadHtml(html) {
  const blob = new Blob([html], { type: "text/html" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "signature.html";
  a.click();
  URL.revokeObjectURL(a.href);
}

function loadHtml(apply) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".html,.htm,text/html";
  input.onchange = () => {
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => apply(String(reader.result || ""));
    reader.readAsText(file);
  };
  input.click();
}
